import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import FloatingDock from '@/components/layout/FloatingDock';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Slider } from '@/components/ui/slider';
import { 
  Target, Diamond, MapPin, Calendar, Clock, Users, 
  TrendingUp, BarChart3, Zap, Play, Pause, Trash2,
  ChevronRight, Plus, X, Check, AlertCircle, Eye,
  DollarSign, Globe, Filter, Sparkles, ArrowLeft
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Interest options
const INTEREST_OPTIONS = [
  'Music', 'Food & Dining', 'Sports', 'Art & Culture', 'Nightlife',
  'Shopping', 'Fitness', 'Education', 'Tech & Gaming', 'Family',
  'Outdoor', 'Community', 'Fashion', 'Travel', 'Health'
];

const AdvancedLocale = ({ user }) => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [localePosts, setLocalePosts] = useState([]);
  const [peakTimes, setPeakTimes] = useState(null);
  const [audienceInsights, setAudienceInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedLocale, setSelectedLocale] = useState(null);
  
  // Campaign form
  const [campaignForm, setCampaignForm] = useState({
    locale_id: '',
    campaign_name: '',
    age_min: 18,
    age_max: 65,
    genders: ['all'],
    interests: [],
    location_radius_km: 50,
    scheduled_times: [],
    budget: '',
    start_date: '',
    end_date: '',
    priority_boost: false
  });
  const [creating, setCreating] = useState(false);
  
  const isSupremium = user?.subscription_tier === 'supremium' || user?.is_admin;

  useEffect(() => {
    if (isSupremium) {
      fetchData();
    }
  }, [isSupremium]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [campaignsRes, postsRes, peakRes, insightsRes] = await Promise.all([
        axios.get(`${API_URL}/locale/advanced/campaigns`, { withCredentials: true }),
        axios.get(`${API_URL}/locale/`, { withCredentials: true }),
        axios.get(`${API_URL}/locale/advanced/peak-times`, { withCredentials: true }),
        axios.get(`${API_URL}/locale/advanced/audience-insights`, { withCredentials: true })
      ]);
      
      setCampaigns(campaignsRes.data.campaigns || []);
      setLocalePosts(postsRes.data.posts?.filter(p => p.user_id === user?.user_id) || []);
      setPeakTimes(peakRes.data);
      setAudienceInsights(insightsRes.data);
    } catch (error) {
      console.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const createCampaign = async () => {
    if (!campaignForm.locale_id) {
      toast.error('Please select a locale post');
      return;
    }
    
    setCreating(true);
    try {
      await axios.post(`${API_URL}/locale/advanced/campaign`, {
        locale_id: campaignForm.locale_id,
        campaign_name: campaignForm.campaign_name,
        target_audience: {
          age_min: campaignForm.age_min,
          age_max: campaignForm.age_max,
          genders: campaignForm.genders,
          interests: campaignForm.interests,
          location_radius_km: campaignForm.location_radius_km
        },
        scheduled_times: campaignForm.scheduled_times,
        budget: campaignForm.budget ? parseFloat(campaignForm.budget) : null,
        start_date: campaignForm.start_date || null,
        end_date: campaignForm.end_date || null,
        priority_boost: campaignForm.priority_boost
      }, { withCredentials: true });
      
      toast.success('Campaign created!');
      setShowCreateModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create campaign');
    } finally {
      setCreating(false);
    }
  };

  const toggleCampaignStatus = async (campaignId, currentStatus) => {
    try {
      const endpoint = currentStatus === 'active' ? 'pause' : 'resume';
      await axios.post(`${API_URL}/locale/advanced/campaigns/${campaignId}/${endpoint}`, {}, { withCredentials: true });
      toast.success(`Campaign ${endpoint === 'pause' ? 'paused' : 'resumed'}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update campaign');
    }
  };

  const deleteCampaign = async (campaignId) => {
    if (!confirm('Delete this campaign?')) return;
    
    try {
      await axios.delete(`${API_URL}/locale/advanced/campaigns/${campaignId}`, { withCredentials: true });
      toast.success('Campaign deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete campaign');
    }
  };

  const scheduleOptimal = async (localeId) => {
    const date = prompt('Enter target date (YYYY-MM-DD):', format(new Date(), 'yyyy-MM-dd'));
    if (!date) return;
    
    try {
      const response = await axios.post(
        `${API_URL}/locale/advanced/schedule-optimal?locale_id=${localeId}&target_date=${date}`,
        {},
        { withCredentials: true }
      );
      toast.success(`Scheduled for ${response.data.scheduled_time} - Added to calendar!`);
    } catch (error) {
      toast.error('Failed to schedule');
    }
  };

  const resetForm = () => {
    setCampaignForm({
      locale_id: '',
      campaign_name: '',
      age_min: 18,
      age_max: 65,
      genders: ['all'],
      interests: [],
      location_radius_km: 50,
      scheduled_times: [],
      budget: '',
      start_date: '',
      end_date: '',
      priority_boost: false
    });
  };

  const toggleGender = (gender) => {
    if (gender === 'all') {
      setCampaignForm({ ...campaignForm, genders: ['all'] });
    } else {
      const current = campaignForm.genders.filter(g => g !== 'all');
      const updated = current.includes(gender) 
        ? current.filter(g => g !== gender)
        : [...current, gender];
      setCampaignForm({ ...campaignForm, genders: updated.length ? updated : ['all'] });
    }
  };

  const toggleInterest = (interest) => {
    const current = campaignForm.interests;
    const updated = current.includes(interest)
      ? current.filter(i => i !== interest)
      : [...current, interest];
    setCampaignForm({ ...campaignForm, interests: updated });
  };

  // Non-Supremium upgrade prompt
  if (!isSupremium) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-white to-amber-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <div className="max-w-2xl mx-auto px-4 py-20 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center mx-auto mb-6">
              <Target className="w-12 h-12 text-white" />
            </div>
            <h1 className="text-4xl font-heading font-black text-gray-900 dark:text-white mb-4">
              Advanced Locale
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-400 mb-8">
              Targeted advertising for <span className="text-yellow-600 font-bold">Supremium Verified</span>
            </p>
            
            <div className="bg-white dark:bg-gray-800 rounded-3xl p-8 shadow-xl border border-yellow-200 mb-8">
              <h3 className="font-bold text-lg mb-4">Advanced Features Include:</h3>
              <ul className="space-y-3 text-left max-w-md mx-auto">
                {[
                  'Target by age, gender, interests',
                  'Location-based targeting',
                  'Peak time scheduling',
                  'Audience insights & analytics',
                  'Priority boost for visibility',
                  'Campaign management dashboard'
                ].map((feature, i) => (
                  <li key={i} className="flex items-center gap-3 text-gray-700 dark:text-gray-300">
                    <Check className="w-5 h-5 text-yellow-500" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
            
            <Button
              onClick={() => navigate('/pricing')}
              className="rounded-full bg-gradient-to-r from-yellow-500 to-amber-500 text-white font-bold px-8 h-14 text-lg"
            >
              <Diamond className="w-5 h-5 mr-2" />
              Upgrade to Supremium - $49/month
            </Button>
          </motion.div>
        </div>
        <FloatingDock user={user} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-white to-amber-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-white/80 dark:bg-gray-900/80 backdrop-blur-lg border-b border-yellow-200">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button onClick={() => navigate(-1)} className="p-2 rounded-full hover:bg-gray-100">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center">
                <Target className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-heading font-bold flex items-center gap-2">
                  Advanced Locale
                  <Diamond className="w-5 h-5 text-yellow-500" />
                </h1>
                <p className="text-sm text-gray-500">Targeted advertising & analytics</p>
              </div>
            </div>
            
            <Button
              onClick={() => setShowCreateModal(true)}
              className="rounded-full bg-gradient-to-r from-yellow-500 to-amber-500 text-white font-bold"
              data-testid="create-campaign-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Campaign
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6 pb-24">
        {/* Audience Insights */}
        {audienceInsights && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card className="p-4 bg-white dark:bg-gray-800 rounded-2xl">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-xl">
                  <Eye className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{audienceInsights.total_views?.toLocaleString()}</p>
                  <p className="text-sm text-gray-500">Total Views</p>
                </div>
              </div>
            </Card>
            <Card className="p-4 bg-white dark:bg-gray-800 rounded-2xl">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-100 rounded-xl">
                  <TrendingUp className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{audienceInsights.total_interested}</p>
                  <p className="text-sm text-gray-500">Interested</p>
                </div>
              </div>
            </Card>
            <Card className="p-4 bg-white dark:bg-gray-800 rounded-2xl">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-xl">
                  <BarChart3 className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{audienceInsights.insights?.engagement_rate || 0}%</p>
                  <p className="text-sm text-gray-500">Engagement</p>
                </div>
              </div>
            </Card>
            <Card className="p-4 bg-white dark:bg-gray-800 rounded-2xl">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-xl">
                  <Users className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{campaigns.length}</p>
                  <p className="text-sm text-gray-500">Campaigns</p>
                </div>
              </div>
            </Card>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Campaigns List */}
          <div className="lg:col-span-2 space-y-4">
            <h2 className="text-xl font-bold">Your Campaigns</h2>
            
            {loading ? (
              <div className="space-y-4">
                {[1, 2].map(i => (
                  <div key={i} className="h-32 bg-gray-200 rounded-2xl animate-pulse" />
                ))}
              </div>
            ) : campaigns.length === 0 ? (
              <Card className="p-8 text-center bg-white rounded-2xl border-dashed border-2 border-yellow-300">
                <Target className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-2">No campaigns yet</h3>
                <p className="text-gray-600 mb-4">Create your first targeted campaign</p>
                <Button onClick={() => setShowCreateModal(true)} className="rounded-full bg-gradient-to-r from-yellow-500 to-amber-500 text-white">
                  <Plus className="w-4 h-4 mr-2" /> Create Campaign
                </Button>
              </Card>
            ) : (
              campaigns.map(campaign => (
                <Card key={campaign.campaign_id} className="p-4 bg-white dark:bg-gray-800 rounded-2xl">
                  <div className="flex items-start justify-between">
                    <div className="flex gap-4">
                      {campaign.locale_post?.image_url ? (
                        <img src={campaign.locale_post.image_url} alt="" className="w-20 h-20 rounded-xl object-cover" />
                      ) : (
                        <div className="w-20 h-20 rounded-xl bg-yellow-100 flex items-center justify-center">
                          <MapPin className="w-8 h-8 text-yellow-500" />
                        </div>
                      )}
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-bold">{campaign.campaign_name}</h3>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                            campaign.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                          }`}>
                            {campaign.status}
                          </span>
                          {campaign.priority_boost && (
                            <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-700">
                              <Zap className="w-3 h-3 inline mr-1" />Boosted
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500 mb-2">{campaign.locale_post?.title}</p>
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                          <span><Eye className="w-4 h-4 inline mr-1" />{campaign.impressions}</span>
                          <span><TrendingUp className="w-4 h-4 inline mr-1" />{campaign.clicks}</span>
                          <span>Age: {campaign.target_audience?.age_min}-{campaign.target_audience?.age_max}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => toggleCampaignStatus(campaign.campaign_id, campaign.status)}
                        className="rounded-full"
                      >
                        {campaign.status === 'active' ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => deleteCampaign(campaign.campaign_id)}
                        className="rounded-full text-red-500"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))
            )}
          </div>

          {/* Peak Times & Quick Actions */}
          <div className="space-y-4">
            {/* Peak Times */}
            {peakTimes && (
              <Card className="p-4 bg-white dark:bg-gray-800 rounded-2xl">
                <h3 className="font-bold mb-4 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-yellow-500" />
                  Peak Posting Times
                </h3>
                <div className="space-y-2">
                  {peakTimes.weekday?.slice(0, 4).map((time, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <span className="text-sm">{time.label}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold">{time.time}</span>
                        <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-yellow-400 to-amber-500 rounded-full"
                            style={{ width: `${time.score}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-3">Best days: {peakTimes.best_days?.join(', ')}</p>
              </Card>
            )}

            {/* Quick Schedule */}
            <Card className="p-4 bg-white dark:bg-gray-800 rounded-2xl">
              <h3 className="font-bold mb-4 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-yellow-500" />
                Quick Schedule
              </h3>
              <p className="text-sm text-gray-500 mb-3">Schedule a post at optimal time</p>
              {localePosts.slice(0, 3).map(post => (
                <button
                  key={post.locale_id}
                  onClick={() => scheduleOptimal(post.locale_id)}
                  className="w-full p-2 mb-2 text-left rounded-xl hover:bg-yellow-50 dark:hover:bg-yellow-900/20 transition-colors flex items-center justify-between"
                >
                  <span className="text-sm truncate">{post.title}</span>
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                </button>
              ))}
            </Card>

            {/* Audience Demographics */}
            {audienceInsights?.insights && (
              <Card className="p-4 bg-white dark:bg-gray-800 rounded-2xl">
                <h3 className="font-bold mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-yellow-500" />
                  Audience Demographics
                </h3>
                <div className="space-y-3">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Age Groups</p>
                    {Object.entries(audienceInsights.insights.demographics.age_groups).map(([age, pct]) => (
                      <div key={age} className="flex items-center gap-2 text-sm">
                        <span className="w-12">{age}</span>
                        <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div className="h-full bg-blue-500 rounded-full" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="w-8 text-right">{pct}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Create Campaign Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 overflow-y-auto"
            onClick={() => setShowCreateModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white dark:bg-gray-800 rounded-3xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 shadow-2xl"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold">Create Campaign</h2>
                <button onClick={() => setShowCreateModal(false)} className="p-2 hover:bg-gray-100 rounded-full">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                {/* Select Locale Post */}
                <div>
                  <label className="block text-sm font-semibold mb-2">Select Locale Post *</label>
                  <select
                    value={campaignForm.locale_id}
                    onChange={(e) => setCampaignForm({ ...campaignForm, locale_id: e.target.value })}
                    className="w-full p-3 rounded-xl border-2 bg-white dark:bg-gray-700"
                  >
                    <option value="">Choose a post...</option>
                    {localePosts.map(post => (
                      <option key={post.locale_id} value={post.locale_id}>{post.title}</option>
                    ))}
                  </select>
                </div>

                {/* Campaign Name */}
                <div>
                  <label className="block text-sm font-semibold mb-2">Campaign Name</label>
                  <Input
                    value={campaignForm.campaign_name}
                    onChange={(e) => setCampaignForm({ ...campaignForm, campaign_name: e.target.value })}
                    placeholder="My Campaign"
                    className="rounded-xl"
                  />
                </div>

                {/* Age Range */}
                <div>
                  <label className="block text-sm font-semibold mb-2">
                    Age Range: {campaignForm.age_min} - {campaignForm.age_max}
                  </label>
                  <div className="flex gap-4">
                    <Input
                      type="number"
                      value={campaignForm.age_min}
                      onChange={(e) => setCampaignForm({ ...campaignForm, age_min: parseInt(e.target.value) })}
                      min={13}
                      max={100}
                      className="rounded-xl"
                    />
                    <Input
                      type="number"
                      value={campaignForm.age_max}
                      onChange={(e) => setCampaignForm({ ...campaignForm, age_max: parseInt(e.target.value) })}
                      min={13}
                      max={100}
                      className="rounded-xl"
                    />
                  </div>
                </div>

                {/* Gender */}
                <div>
                  <label className="block text-sm font-semibold mb-2">Target Gender</label>
                  <div className="flex flex-wrap gap-2">
                    {['all', 'male', 'female', 'other'].map(gender => (
                      <button
                        key={gender}
                        onClick={() => toggleGender(gender)}
                        className={`px-4 py-2 rounded-full text-sm font-semibold capitalize ${
                          campaignForm.genders.includes(gender)
                            ? 'bg-yellow-500 text-white'
                            : 'bg-gray-100 dark:bg-gray-700'
                        }`}
                      >
                        {gender}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Interests */}
                <div>
                  <label className="block text-sm font-semibold mb-2">Target Interests</label>
                  <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                    {INTEREST_OPTIONS.map(interest => (
                      <button
                        key={interest}
                        onClick={() => toggleInterest(interest)}
                        className={`px-3 py-1 rounded-full text-sm ${
                          campaignForm.interests.includes(interest)
                            ? 'bg-yellow-500 text-white'
                            : 'bg-gray-100 dark:bg-gray-700'
                        }`}
                      >
                        {interest}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Location Radius */}
                <div>
                  <label className="block text-sm font-semibold mb-2">
                    Location Radius: {campaignForm.location_radius_km} km
                  </label>
                  <input
                    type="range"
                    min={5}
                    max={200}
                    value={campaignForm.location_radius_km}
                    onChange={(e) => setCampaignForm({ ...campaignForm, location_radius_km: parseInt(e.target.value) })}
                    className="w-full"
                  />
                </div>

                {/* Priority Boost */}
                <div className="flex items-center justify-between p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-xl">
                  <div className="flex items-center gap-2">
                    <Zap className="w-5 h-5 text-yellow-500" />
                    <span className="font-semibold">Priority Boost</span>
                  </div>
                  <button
                    onClick={() => setCampaignForm({ ...campaignForm, priority_boost: !campaignForm.priority_boost })}
                    className={`w-12 h-6 rounded-full transition-colors ${
                      campaignForm.priority_boost ? 'bg-yellow-500' : 'bg-gray-300'
                    }`}
                  >
                    <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${
                      campaignForm.priority_boost ? 'translate-x-6' : 'translate-x-0.5'
                    }`} />
                  </button>
                </div>

                <Button
                  onClick={createCampaign}
                  disabled={creating || !campaignForm.locale_id}
                  className="w-full rounded-full bg-gradient-to-r from-yellow-500 to-amber-500 text-white font-bold h-12 mt-4"
                >
                  {creating ? 'Creating...' : 'Create Campaign'}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <FloatingDock user={user} />
    </div>
  );
};

export default AdvancedLocale;
